import React, { useState, useEffect, useRef, useCallback } from 'react'
import {
  Box,
  Button,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Grid,
  IconButton,
  CircularProgress,
  Chip
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import CancelIcon from '@mui/icons-material/Cancel'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import CustomAlert from '../_WMS/AlertMessage/AlertMessage'
import { FormData } from './types'
import { VasRuleSku } from '../../interfaces/VasRule/VasRule'
import { useApi } from '../../hooks/useApi'
import { ApiResponse } from '../../typings/ApiResponse'
import {
  VasRuleDetail,
  VasRuleOrderDetailCreate
} from '../../interfaces/VasRule/VasRule'
import {
  getVasRuleDetailList,
  createVasRuleOrderDetail
} from '../../pages/VasRule/config/api'
import { SkuVasDetail } from './types'
import { eTypeData } from '../../enums/eTypeData'
import { useStyles } from './styles'
import { CaptureVasHeader } from './CaptureVasHeader'
import { ModalConfirmDelete } from './ModalConfirmDelete'
import { Status } from '../../hooks/useFetchState'
import { LibraryAddCheck, Info, Refresh } from '@mui/icons-material'
import { ModalConfirmReset } from './ModalConfirmReset'

interface ComponentProps {
  VasRuleSku: VasRuleSku | undefined
  SkuVasDetailData: SkuVasDetail | undefined
  handleSnackbar?: (message: string, success: boolean) => void
  handleCloseModal?: () => void // Nueva prop para cerrar el modal
  onSaveSuccess?: () => void // Callback cuando se guarda exitosamente
}

export const CaptureVas: React.FC<ComponentProps> = ({
  VasRuleSku,
  SkuVasDetailData,
  handleSnackbar,
  handleCloseModal,
  onSaveSuccess
}) => {
  const classes = useStyles()

  // Estados
  const [fields, setFields] = useState<VasRuleDetail[]>([])
  const [formData, setFormData] = useState<FormData>({})
  const [tableData, setTableData] = useState<FormData[]>([])
  const [vasDetailsToSave, setVasDetailsToSave] = useState<
    VasRuleOrderDetailCreate[]
  >([])
  const [saving, setSaving] = useState<boolean>(false)
  const [error, setError] = useState<string>('')
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false)
  const [rowToDelete, setRowToDelete] = useState<number | null>(null)
  const { callApi: getVasRuleDetail, state: vasRuleDetailState } =
    useApi<ApiResponse<VasRuleDetail>>()
  const { callApi: postVasRuleOrderDetail } = useApi()

  // Calcular cantidad restante
  const remainingQuantity =
    (SkuVasDetailData?.validatedQty || 0) - tableData.length

  // Determinar el color del contador según la cantidad restante
  const getCounterClass = () => {
    const percentage =
      (remainingQuantity / (SkuVasDetailData?.validatedQty || 0)) * 100

    if (remainingQuantity === 0) {
      return classes.counterValueSuccess
    } else if (percentage <= 25) {
      return classes.counterValueDanger
    } else if (percentage <= 50) {
      return classes.counterValueWarning
    }
    return classes.counterValue
  }

  // Obtener el estado del progreso
  const getProgressStatus = () => {
    if (remainingQuantity === 0) {
      return {
        label: '✅ Completado',
        color: 'primary' as const
      }
    } else if (
      remainingQuantity <
      (SkuVasDetailData?.validatedQty || 0) * 0.25
    ) {
      return {
        label: 'ⓘ Casi completo',
        color: 'secondary' as const
      }
    } else if (
      remainingQuantity <
      (SkuVasDetailData?.validatedQty || 0) * 0.5
    ) {
      return { label: 'En progreso', color: 'default' as const }
    }
    return { label: 'Iniciando', color: 'default' as const }
  }

  // Llamada a API
  useEffect(() => {
    fetchApiFields()
  }, [VasRuleSku?.vasRuleId])

  const fetchApiFields = async () => {
    const vasRuleId =
      VasRuleSku && VasRuleSku.vasRuleId ? VasRuleSku.vasRuleId : 0
    getVasRuleDetail(getVasRuleDetailList(vasRuleId, 0, 100))
      .then(() => {
        const initialFormData: FormData = {}
        fields.forEach((field: any) => {
          initialFormData[field.name] = ''
        })
        setFormData(initialFormData)
      })
      .catch((err) => {
        setError('Error al cargar los campos del formulario')
        showSnackbar(`${err}` || 'Error al cargar los campos', 'error')
      })
  }

  useEffect(() => {
    setFields(vasRuleDetailState.data?.items || [])
  }, [vasRuleDetailState.data])

  // Mostrar notificación
  const showSnackbar = (
    message: string,
    severity: 'success' | 'error' | 'info'
  ) => {
    if (handleSnackbar) {
      const success = severity === 'success'
      handleSnackbar(message, success)
    }
  }

  // Manejar cambios en los inputs
  const handleInputChange = (fieldName: string, value: string | number) => {
    setFormData((prev: any) => ({
      ...prev,
      [fieldName]: value
    }))
  }

  // Validar formulario
  const validateForm = (): boolean => {
    if (editingIndex === null && remainingQuantity <= 0) {
      showSnackbar(
        `No puedes agregar más registros. Ya has alcanzado el límite de ${
          SkuVasDetailData?.validatedQty || 0
        } registros.`,
        'error'
      )
      return false
    }

    for (const field of fields) {
      const value = formData[field.name]

      if (!value || value === '') {
        showSnackbar(`El campo "${field.name}" es obligatorio`, 'error')
        return false
      }

      if (field.typeDataId === eTypeData.Numérico && value !== '') {
        const numValue = Number(value)
        if (field.minLength !== undefined && numValue < field.minLength) {
          showSnackbar(
            `El campo "${field.name}" debe ser mayor o igual a ${field.minLength}`,
            'error'
          )
          return false
        }
        if (field.maxLength !== undefined && numValue > field.maxLength) {
          showSnackbar(
            `El campo "${field.name}" debe ser menor o igual a ${field.maxLength}`,
            'error'
          )
          return false
        }
      }

      if (field.isUnique) {
        const currentValue = String(formData[field.name] || '')
          .trim()
          .toLowerCase()
        if (currentValue !== '') {
          const existsInOtherRow = tableData.some((row, index) => {
            if (editingIndex !== null && index === editingIndex) return false
            return (
              String(row[field.name] || '').trim().toLowerCase() === currentValue
            )
          })
          if (existsInOtherRow) {
            showSnackbar(
              `El valor "${formData[field.name]}" ya existe en otro registro para el campo "${field.name}"`,
              'error'
            )
            return false
          }
        }
      }
    }

    return true
  }

  // Agregar datos a la tabla
  const handleAddToTable = () => {
    if (!validateForm()) {
      return
    }

    if (editingIndex !== null) {
      const updatedData = [...tableData]
      updatedData[editingIndex] = { ...formData }
      setTableData(updatedData)

      const updatedVasDetails = [...vasDetailsToSave]
      const filteredDetails = updatedVasDetails.filter(
        (detail) => detail.indexDetail !== editingIndex + 1
      )

      const newDetails: VasRuleOrderDetailCreate[] = fields.map((field) => ({
        orderDetailId: SkuVasDetailData?.orderDetailId || 0,
        vasRuleDetailId: field.vasRuleDetailId,
        value: formData[field.name],
        indexDetail: editingIndex + 1
      }))

      setVasDetailsToSave(
        [...filteredDetails, ...newDetails].sort(
          (a, b) =>
            a.indexDetail - b.indexDetail ||
            a.vasRuleDetailId - b.vasRuleDetailId
        )
      )

      setEditingIndex(null)
      showSnackbar('Registro actualizado correctamente', 'success')
    } else {
      const newIndex = tableData.length
      setTableData((prev) => [...prev, { ...formData }])

      const newVasDetails: VasRuleOrderDetailCreate[] = fields.map((field) => ({
        orderDetailId: SkuVasDetailData?.orderDetailId || 0,
        vasRuleDetailId: field.vasRuleDetailId,
        value: formData[field.name],
        indexDetail: newIndex + 1
      }))

      setVasDetailsToSave((prev) => [...prev, ...newVasDetails])
    }

    clearForm()
  }

  // Limpiar formulario
  const clearForm = () => {
    const clearedFormData: FormData = {}
    fields.forEach((field) => {
      clearedFormData[field.name] = ''
    })
    setFormData(clearedFormData)
    setEditingIndex(null)

    // Auto-focus en el primer campo si quedan registros por capturar
    setTimeout(() => {
      if (remainingQuantity > 0) {
        const firstField = fieldRefs.current.get(0)
        if (firstField) {
          firstField.focus()
        }
      }
    }, 50)
  }

  // Limpiar todos los estados del componente
  const clearAllStates = () => {
    setTableData([])
    setVasDetailsToSave([])
    clearForm()
  }

  // Abrir modal de confirmación para reinicio de captura VAS
  const handleResetClick = () => {
    setResetConfirmOpen(true)
  }

  // Evento para reiniciar captura de VAS
  const handleReset = () => {
    clearAllStates()
    setResetConfirmOpen(false)
  }

  // Editar fila de la tabla
  const handleEditRow = (index: number) => {
    setFormData({ ...tableData[index] })
    setEditingIndex(index)
  }

  // Cancelar edición
  const handleCancelEdit = () => {
    clearForm()
  }

  // Eliminar fila de la tabla
  const handleDeleteRowClick = (index: number) => {
    setRowToDelete(index)
    setDeleteConfirmOpen(true)
  }

  const handleDeleteRow = () => {
    if (rowToDelete === null) return

    const index = rowToDelete

    setTableData((prev) => prev.filter((_, i) => i !== index))

    setVasDetailsToSave((prev) => {
      const filtered = prev.filter((detail) => detail.indexDetail !== index + 1)

      return filtered.map((detail) => ({
        ...detail,
        indexDetail:
          detail.indexDetail > index + 1
            ? detail.indexDetail - 1
            : detail.indexDetail
      }))
    })

    showSnackbar(
      `Registro eliminado. Ahora quedan ${remainingQuantity + 1} por capturar`,
      'success'
    )

    if (editingIndex === index) {
      clearForm()
    }

    setDeleteConfirmOpen(false)
    setRowToDelete(null)
  }

  const handleCancelReset = () => {
    setResetConfirmOpen(false)
  }

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false)
    setRowToDelete(null)
  }

  // Guardar todos los datos en la API
  const handleSaveToAPI = async () => {
    if (vasDetailsToSave.length === 0) {
      showSnackbar('No hay datos para guardar', 'error')
      return
    }
    setSaving(true)
    postVasRuleOrderDetail(createVasRuleOrderDetail(vasDetailsToSave))
      .then(() => {
        clearAllStates()

        // Llamar al callback si existe
        if (onSaveSuccess) {
          onSaveSuccess()
        }

        // Cerrar el modal si la función está disponible
        if (handleCloseModal) {
          handleCloseModal()
        }

        showSnackbar('Datos guardados exitosamente', 'success')
      })
      .catch((error) => {
        showSnackbar(`${error}` || 'Error al guardar los datos', 'error')
      })
      .finally(() => setSaving(false))
  }

  const fieldRefs = useRef<Map<number, HTMLInputElement>>(new Map())

  const setFieldRef = useCallback(
    (index: number, element: HTMLInputElement | null) => {
      if (element) {
        fieldRefs.current.set(index, element)
      } else {
        fieldRefs.current.delete(index)
      }
    },
    []
  )

  const focusNextField = useCallback((currentIndex: number) => {
    const nextIndex = currentIndex + 1
    const nextField = fieldRefs.current.get(nextIndex)

    if (nextField) {
      // Pequeño delay para asegurar el render
      setTimeout(() => {
        nextField.focus()
        nextField.select() // Opcional: seleccionar el contenido
      }, 0)
    }
  }, [])

  // Recolectar valores capturados para un campo específico (excluyendo fila en edición)
  const getAllCapturedValues = useCallback(
    (fieldName?: string): Set<string> => {
      // Solo los valores del mismo campo en otras filas guardadas
      const savedValues = tableData.flatMap((row, index) => {
        if (editingIndex !== null && index === editingIndex) return []
        if (fieldName) {
          const val = String(row[fieldName] || '').trim().toLowerCase()
          return val !== '' ? [val] : []
        }
        return Object.values(row).map((val) => String(val).trim().toLowerCase())
      })

      // Valores de otros campos únicos en el formulario actual (excluyendo el campo que se está escribiendo)
      const currentFormValues = fields
        .filter((f) => f.name !== fieldName && f.isUnique)
        .map((f) =>
          String(formData[f.name] || '')
            .trim()
            .toLowerCase()
        )
        .filter((v) => v !== '')

      return new Set([...savedValues, ...currentFormValues])
    },
    [tableData, editingIndex, fields, formData]
  )

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    currentIndex: number
  ) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      event.stopPropagation()

      const isLastField = currentIndex === fields.length - 1
      if (isLastField) {
        if (remainingQuantity > 0 || editingIndex !== null) {
          handleAddToTable()
        }
      } else {
        focusNextField(currentIndex)
      }
    }
  }

  // Limpiar los estados cuando se cancela la captura (cuando se cierra el modal)
  const cancelCapture = () => {
    setTableData([])
    setVasDetailsToSave([])
    clearForm()
    handleCloseModal && handleCloseModal()
  }

  // Omitir caracteres específicos permitidos en campo de tipo numérico
  const handleKeyDownTypeNumberTextField = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (['e', 'E', '+', '-'].includes(e.key)) {
      e.preventDefault()
    }
  }

  // Validar longitudes mínima y máxima de campo numérico, acorde a configuración de datos VAS
  const handleChangeTypeNumberTextField = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void,
    minLength?: number,
    maxLength?: number,
    fieldName?: string,
    isUnique?: boolean
  ) => {
    const target = e.target as HTMLInputElement
    const value = parseInt(target.value)

    if (value <= 0 || isNaN(value)) {
      target.value = ''
    } else if (maxLength && value > maxLength) {
      target.value = String(maxLength)
    } else if (minLength && value < minLength) {
      target.value = String(minLength)
    }

    if (isUnique && target.value !== '' && fieldName) {
      const allCapturedValues = getAllCapturedValues(fieldName)
      if (allCapturedValues.has(target.value.trim().toLowerCase())) {
        showSnackbar(
          `El valor "${target.value}" ya fue capturado en otro campo o fila.`,
          'error'
        )
        target.value = ''
        e.target.value = ''
        onChange?.(e as React.ChangeEvent<HTMLInputElement>)
        return
      }
    }

    onChange?.(e as React.ChangeEvent<HTMLInputElement>)
  }

  // Handler para campo alfanumérico con validación de duplicados
  const handleChangeTextField = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    fieldName: string,
    isUnique?: boolean
  ) => {
    const target = e.target as HTMLInputElement
    const value = target.value

    if (isUnique && value !== '') {
      const allCapturedValues = getAllCapturedValues(fieldName)
      if (allCapturedValues.has(value.trim().toLowerCase())) {
        showSnackbar(
          `El valor "${value}" ya fue capturado en otro campo o fila.`,
          'error'
        )
        handleInputChange(fieldName, '')
        return
      }
    }

    handleInputChange(fieldName, value)
  }

  // Renderizar campo según tipo
  const renderField = (field: VasRuleDetail, index: number) => {
    const isFormDisabled = editingIndex === null && remainingQuantity <= 0
    const commonProps = {
      fullWidth: true,
      label: field.name,
      value: formData[field.name] || '',
      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        handleInputChange(field.name, e.target.value),
      required: true,
      variant: 'outlined' as const,
      className: classes.formField,
      placeholder: field.name,
      inputRef: (el: HTMLInputElement) => setFieldRef(index, el),
      onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) =>
        handleKeyDown(e, index),
      disabled: isFormDisabled
    }

    switch (field.typeDataId) {
      case eTypeData.Numérico:
        return (
          <TextField
            {...commonProps}
            type='number'
            inputProps={{
              min: field.minLength,
              max: field.maxLength,
              onKeyDown: handleKeyDownTypeNumberTextField
            }}
            onChange={(e) =>
              handleChangeTypeNumberTextField(
                e,
                commonProps.onChange,
                field.minLength,
                field.maxLength,
                field.name,
                field.isUnique
              )
            }
          />
        )
      case eTypeData.Alfanumérico:
        return (
          <TextField
            {...commonProps}
            type='text'
            inputProps={{
              minLength: field.minLength,
              maxLength: field.maxLength
            }}
            onChange={(e) => handleChangeTextField(e, field.name, field.isUnique)}
          />
        )
      default:
        return (
          <TextField
            {...commonProps}
            type='text'
            inputProps={{
              minLength: field.minLength,
              maxLength: field.maxLength
            }}
            onChange={(e) => handleChangeTextField(e, field.name, field.isUnique)}
          />
        )
    }
  }

  if (
    vasRuleDetailState.status === Status.Loading ||
    !vasRuleDetailState.data
  ) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='400px'
      >
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box className={classes.root}>
        <CustomAlert severity='error' message={error} />
      </Box>
    )
  }

  const progressStatus = getProgressStatus()

  return (
    <Box className={classes.root}>
      {/* Encabezado Full Width con información del producto */}
      {VasRuleSku && SkuVasDetailData && (
        <CaptureVasHeader
          VasRuleSku={VasRuleSku}
          SkuVasDetailData={SkuVasDetailData}
        />
      )}

      {/* Contenido con padding */}
      <Box className={classes.content}>
        {/* Contador de registros */}
        <Paper className={classes.counterContainer} elevation={3}>
          <Box className={classes.counterBox}>
            <Typography className={classes.counterLabel}>Total</Typography>
            <Typography className={classes.counterValue}>
              {SkuVasDetailData?.validatedQty || 0}
            </Typography>
          </Box>

          <Box className={classes.divider} />

          <Box className={classes.counterBox}>
            <Typography className={classes.counterLabel}>Capturados</Typography>
            <Typography className={classes.counterValueSuccess}>
              {tableData.length}
            </Typography>
          </Box>

          <Box className={classes.divider} />

          <Box className={classes.counterBox}>
            <Typography className={classes.counterLabel}>Restantes</Typography>
            <Typography className={getCounterClass()}>
              {remainingQuantity}
            </Typography>
          </Box>

          <Box className={classes.divider} />

          <Box className={classes.counterBox}>
            <Typography className={classes.counterLabel}>Estado</Typography>
            <Chip
              label={progressStatus.label}
              color={progressStatus.color}
              className={classes.statusChip}
            />
          </Box>
        </Paper>

        {/* Alerta si está cerca del límite o completo */}
        {remainingQuantity === 0 && (
          <CustomAlert
            severity='success'
            icon={<LibraryAddCheck />}
            message={`¡Excelente! Has completado la captura de todos los
            ${SkuVasDetailData?.validatedQty || 0} registros.`}
          />
        )}

        {remainingQuantity > 0 && remainingQuantity <= 3 && (
          <CustomAlert
            severity='info'
            icon={<Info />}
            message={`Te quedan solo ${remainingQuantity} registro(s) por capturar.`}
          />
        )}

        {/* Formulario */}
        <Paper className={classes.formContainer} elevation={3}>
          <Typography variant='h6' gutterBottom>
            {editingIndex !== null
              ? 'Editar Registro'
              : 'Agregar Nuevo Registro'}
          </Typography>
          <Grid container spacing={2}>
            {fields.map((field, index) => (
              <Grid item xs={12} sm={6} md={4} key={field.name}>
                {renderField(field, index)}
              </Grid>
            ))}
          </Grid>
          <Box className={classes.buttonContainer}>
            <Button
              variant='contained'
              color='primary'
              startIcon={<Refresh />}
              onClick={handleResetClick}
              disabled={remainingQuantity === SkuVasDetailData?.validatedQty}
            >
              Reiniciar Captura
            </Button>
            {editingIndex !== null && (
              <Button
                variant='outlined'
                color='inherit'
                startIcon={<CancelIcon />}
                onClick={handleCancelEdit}
              >
                Cancelar Edición
              </Button>
            )}
            <Button
              variant='contained'
              color='primary'
              startIcon={editingIndex !== null ? <SaveIcon /> : <AddIcon />}
              onClick={handleAddToTable}
              disabled={editingIndex === null && remainingQuantity <= 0}
            >
              {editingIndex !== null ? 'Guardar Cambios' : 'Agregar'}
            </Button>
          </Box>
        </Paper>

        {/* Resumen */}
        {tableData.length > 0 && (
          <Paper className={classes.summaryBox}>
            <Typography variant='subtitle1'>
              <strong>Progreso:</strong> {tableData.length} de{' '}
              {SkuVasDetailData?.validatedQty || 0} registros (
              {(
                (tableData.length / (SkuVasDetailData?.validatedQty || 0)) *
                100
              ).toFixed(1)}
              % completado)
            </Typography>
          </Paper>
        )}

        {/* Tabla */}
        <Paper className={classes.tableContainer} elevation={3}>
          <Box p={2}>
            <Typography variant='h6'>Registros Capturados</Typography>
          </Box>
          {tableData.length === 0 ? (
            <Box className={classes.emptyState}>
              <Typography variant='body1'>
                No hay registros. Agrega datos usando el formulario anterior.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.tableHeader}>#</TableCell>
                    {fields.map((field) => (
                      <TableCell
                        key={field.name}
                        className={classes.tableHeader}
                      >
                        {field.name}
                      </TableCell>
                    ))}
                    <TableCell align='center' className={classes.tableHeader}>
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableData.map((row, index) => (
                    <TableRow
                      key={index}
                      hover
                      selected={editingIndex === index}
                    >
                      <TableCell>{index + 1}</TableCell>
                      {fields.map((field) => (
                        <TableCell key={field.name}>
                          {row[field.name]?.toString() || '-'}
                        </TableCell>
                      ))}
                      <TableCell align='center'>
                        <Box className={classes.actionButtons}>
                          <IconButton
                            color='primary'
                            onClick={() => handleEditRow(index)}
                            size='small'
                            title='Editar'
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            color='secondary'
                            onClick={() => handleDeleteRowClick(index)}
                            size='small'
                            title='Eliminar'
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {/* Botón Cancelar y Botón Guardar Todo al final */}
        <Box className={classes.buttonFooter}>
          <Button
            variant='contained'
            color='primary'
            size='large'
            onClick={cancelCapture}
            className={classes.cancelButton}
          >
            Cancelar
          </Button>
          <Button
            variant='contained'
            color='primary'
            size='large'
            startIcon={<CloudUploadIcon />}
            onClick={handleSaveToAPI}
            disabled={saving || remainingQuantity > 0}
          >
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </Box>
      </Box>
      {/* Modal de confirmación de eliminación */}
      <ModalConfirmDelete
        DeleteConfirmOpen={deleteConfirmOpen}
        HandleCancelDelete={handleCancelDelete}
        HandleDeleteRow={handleDeleteRow}
      />
      {/* Modal de confirmación de eliminación */}
      <ModalConfirmReset
        ResetConfirmOpen={resetConfirmOpen}
        HandleCancelReset={handleCancelReset}
        HandleReset={handleReset}
      />
    </Box>
  )
}
